import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import {
  AccordionComponent,
  AccordionItemDirective,
  AccordionItemsDirective,
} from '@syncfusion/ej2-react-navigations'
import './ViewCampaignStyle.css'
import '../ModalStyle.css'
import { Button } from '@components/common'
import { ICampaignRequest } from '@dataTypes/marketing.type'
import MessagePreviewModal from '../MessagePreview/MessagePreview.component'
import ViewRecipientsModal from '../ViewRecipients/ViewRecipients.component'
import { formatMilitaryTimeTo12Hour } from '@utils/time.util'
import { format } from 'date-fns'
import { TIMEZONE_OPTIONS } from '@constants/Common/date.constant'
import { formatRecurringDays } from '@utils/date.utils'

type ViewCampaignProps = {
  campaign: ICampaignRequest
  show: boolean
  onHide: () => void
}

const ViewCampaignModal: React.FC<ViewCampaignProps> = ({
  campaign,
  show,
  onHide,
}) => {
  const [showMessagePreview, setShowMessagePreview] = useState(false)
  const [showViewRecipients, setShowViewRecipients] = useState(false)

  const getTimePoints = (): JSX.Element => {
    const filteredTP = campaign.campaign_trigger_message.filter(
      (tp) => tp.status === 'Active',
    )
    if (filteredTP.length > 0) {
      return (
        <>
          {filteredTP.map((tp) => (
            <span key={tp.campaign_trigger_message_id}>
              {`${tp.name} (${tp.send_on} ${tp.send_duration} ${tp.send_option.toLowerCase()})`}
            </span>
          ))}
        </>
      )
    }
    return <>-</>
  }

  const getActionTriggers = (): JSX.Element => {
    const filteredAT = campaign.campaign_trigger_keyword.filter(
      (tk) => tk.status === 'Active',
    )
    if (filteredAT.length > 0) {
      return (
        <>
          {filteredAT.map((tk) => (
            <span
              key={tk.campaign_trigger_keyword_id}
            >{`${tk.name} (${tk.keyword})`}</span>
          ))}
        </>
      )
    }
    return <>-</>
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        className="view-campaign-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>View campaign info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AccordionComponent>
            <AccordionItemsDirective>
              <AccordionItemDirective
                key="campaign-info-key"
                expanded={true}
                header="Campaign info"
                content={() => (
                  <div id="campaign-info-content">
                    <div className="rev-item">
                      <span className="rev-item-label">Campaign name</span>
                      <span className="rev-item-value">{campaign.name}</span>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Campaign type</span>
                      <span className="rev-item-value">{campaign.type}</span>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Send via</span>
                      <span className="rev-item-value">{campaign.channel}</span>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Message</span>
                      <button
                        type="button"
                        className="unstyled-button rev-item-value-link"
                        onClick={() => setShowMessagePreview(true)}
                      >
                        Preview
                      </button>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">
                        Selected recipients
                      </span>
                      <button
                        type="button"
                        className="unstyled-button rev-item-value-link"
                        onClick={() => setShowViewRecipients(true)}
                      >
                        View
                      </button>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Description</span>
                      <span className="rev-item-value">
                        {campaign.description}
                      </span>
                    </div>
                  </div>
                )}
              />
              <AccordionItemDirective
                key="scheduling-key"
                expanded={true}
                header="Scheduling"
                content={() => (
                  <div id="scheduling-content">
                    <div className="rev-item">
                      <span className="rev-item-label">
                        Message send option
                      </span>
                      <span className="rev-item-value">
                        {campaign.sending_option}
                        {campaign.sending_option === 'Recurring'
                          ? ` (${campaign.recurring_interval})`
                          : ''}
                      </span>
                    </div>
                    {campaign.sending_option === 'Recurring' ? (
                      <div className="rev-item">
                        <span className="rev-item-label">Recurring days</span>
                        <span className="rev-item-value">
                          {formatRecurringDays(campaign.recurring_days)}
                          {` (${formatMilitaryTimeTo12Hour(campaign.recurring_time)} - ${campaign.recurring_duration} ${campaign.recurring_interval === 'Daily' ? 'days' : 'weeks'})`}
                        </span>
                      </div>
                    ) : campaign.sending_option === 'Later' ? (
                      <div className="rev-item">
                        <span className="rev-item-label">Schedule date</span>
                        <span className="rev-item-value">
                          {campaign.schedule_date &&
                            format(
                              new Date(campaign.schedule_date),
                              'MM/dd/yyyy',
                            )}
                          {` - ${formatMilitaryTimeTo12Hour(campaign.schedule_time)}`}
                        </span>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div className="rev-item">
                      <span className="rev-item-label">Timezone</span>
                      <span className="rev-item-value">
                        {TIMEZONE_OPTIONS.find(
                          (option) => option.value === campaign.timezone,
                        )?.label || campaign.timezone}
                      </span>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Period start</span>
                      <span className="rev-item-value">
                        {campaign.start_date &&
                          format(new Date(campaign.start_date), 'MM/dd/yyyy')}
                        {` - ${formatMilitaryTimeTo12Hour(campaign.start_time)}`}
                      </span>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Ends on</span>
                      <span className="rev-item-value">
                        {campaign.end_date &&
                          format(new Date(campaign.end_date), 'MM/dd/yyyy')}
                        {` - ${formatMilitaryTimeTo12Hour(campaign.end_time)}`}
                      </span>
                    </div>
                  </div>
                )}
              />
              <AccordionItemDirective
                key="trigger-events-key"
                expanded={true}
                header="Trigger events"
                content={() => (
                  <div id="trigger-events-content">
                    <div className="rev-item">
                      <span className="rev-item-label">Time points</span>
                      <span className="rev-item-value">{getTimePoints()}</span>
                    </div>
                    <div className="rev-item">
                      <span className="rev-item-label">Action triggers</span>
                      <span className="rev-item-value">
                        {getActionTriggers()}
                      </span>
                    </div>
                  </div>
                )}
              />
            </AccordionItemsDirective>
          </AccordionComponent>
        </Modal.Body>
        <Modal.Footer>
          <Button className="button-secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {(showMessagePreview || showViewRecipients) && (
        <div className="fade modal-backdrop show mpm-custom-backdrop" />
      )}
      {showMessagePreview && campaign.html_content && (
        <MessagePreviewModal
          content={campaign.html_content}
          show={showMessagePreview}
          onHide={() => setShowMessagePreview(false)}
        />
      )}
      {showViewRecipients && campaign.campaign_recipient && (
        <ViewRecipientsModal
          recipients={campaign.campaign_recipient}
          show={showViewRecipients}
          onHide={() => setShowViewRecipients(false)}
        />
      )}
    </>
  )
}

export default ViewCampaignModal
